// M02 Zweiter Faktor (nach dem Passwort) und erzwungene Einrichtung, wenn MFA Pflicht ist (4.2).
import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../../api/client';
import { serverMessage, verifyMfa } from '../../api/account';
import { qk } from '../../api/queries';
import type { Me } from '../../api/types';
import { Field } from '../../components/ui';
import { AuthLayout } from '../AuthLayout';
import { CodeInput, MfaSetupWizard } from './MfaSetup';

function useLogout() {
  const qc = useQueryClient();
  return async () => {
    try {
      await api.post('/auth/logout');
    } catch {
      /* Sitzung ohnehin weg */
    }
    qc.clear();
    qc.setQueryData(qk.me, null);
  };
}

export function LoginMfaPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const logout = useLogout();
  const [recovery, setRecovery] = useState(false);
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const me = await verifyMfa(recovery ? 'recovery' : 'totp', code.trim());
      qc.setQueryData(qk.me, me);
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) setError(t('errors.rate_limited'));
      else setError(serverMessage(err) ?? t('errors.network'));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1>{t('account.verify.title')}</h1>
      <div className="muted">{recovery ? t('account.verify.recoveryText') : t('account.verify.text')}</div>
      <form onSubmit={submit} noValidate>
        {recovery ? (
          <Field label={t('account.verify.recoveryCode')}>
            <input className="mono" autoFocus autoComplete="off" value={code} onChange={(e) => setCode(e.target.value)} placeholder="abcde-fghjk" />
          </Field>
        ) : <CodeInput value={code} onChange={setCode} label={t('account.mfa.code')} />}
        <div className="error" role="alert">{error}</div>
        <button className="btn primary" disabled={busy || (recovery ? code.trim().length < 10 : code.length !== 6)}>{busy ? t('common.loading') : t('account.verify.submit')}</button>
        <div className="row">
          <button type="button" className="link-btn" onClick={() => { setRecovery((x) => !x); setCode(''); setError(''); }}>
            {recovery ? t('account.verify.useApp') : t('account.verify.useRecovery')}
          </button>
          <span className="grow" />
          <button type="button" className="link-btn" onClick={() => void logout()}>{t('account.verify.cancel')}</button>
        </div>
      </form>
    </AuthLayout>
  );
}

/** MFA ist Pflicht (Kunde oder Plattform), aber noch nicht eingerichtet: Einrichtung vor allem anderen. */
export function MfaRequiredPage({ me }: { me: Me }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const logout = useLogout();
  return (
    <AuthLayout>
      <h1>{t('account.required.title')}</h1>
      <div className="muted">{t('account.required.text')}</div>
      <div style={{ marginTop: 20 }}>
        <MfaSetupWizard email={me.user.email} onFinished={(next) => qc.setQueryData(qk.me, next)} />
      </div>
      <div className="row" style={{ marginTop: 14 }}>
        <span className="grow" />
        <button type="button" className="link-btn" onClick={() => void logout()}>{t('shell.logout')}</button>
      </div>
    </AuthLayout>
  );
}
